/**
 * Filtro de anúncios pela classificação do produto no ERP (na Qualidoc: departamento › categoria › subcategoria
 * de cada SKU), em cascata. O mesmo em todos os canais.
 */
import { useQuery } from '@tanstack/react-query';
import { api, int } from '../api.ts';

interface Row { department: string | null; category: string | null; subcategory: string | null; total: number; active: number }

const NONE = '__none__';
const KEYS = ['department', 'category', 'subcategory'] as const;

function sum(rows: Row[], key: (typeof KEYS)[number]) {
  const m = new Map<string, { total: number; active: number }>();
  for (const r of rows) {
    const v = r[key];
    if (!v) continue;
    const c = m.get(v) ?? { total: 0, active: 0 };
    c.total += r.total; c.active += r.active;
    m.set(v, c);
  }
  return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'));
}

export function ListingCategorySelect({ channel, accountId, params, set }: {
  channel: string;
  accountId?: string;
  params: URLSearchParams;
  set: (p: Record<string, string | null>) => void;
}) {
  const q = useQuery({
    queryKey: ['listing-categories', channel, accountId ?? null],
    queryFn: () => api<Row[]>(`/channels/listings/categories?${new URLSearchParams({ channel, ...(accountId ? { account: accountId } : {}) })}`),
    staleTime: 60_000,
  });
  const rows = q.data ?? [];
  const dep = params.get('department');
  const cat = params.get('category');
  const sub = params.get('subcategory');
  const none = cat === NONE || dep === NONE;
  const inDep = rows.filter((r) => !dep || r.department === dep);
  const inCat = inDep.filter((r) => !cat || r.category === cat);
  const departments = sum(rows, 'department');
  const categories = sum(inDep, 'category');
  const subcategories = cat && !none ? sum(inCat, 'subcategory') : [];
  const noneCount = rows.filter((r) => !r.department && !r.category).reduce((a, r) => a + r.total, 0);
  const label = (name: string, c: { total: number }) => `${name} · ${int(c.total)}`;
  // Quantos anúncios no recorte escolhido (para responder "quantos genéricos eu tenho?").
  const picked = none ? rows.filter((r) => !r.department && !r.category) : inCat.filter((r) => !sub || r.subcategory === sub);
  const filtered = !!(dep || cat || sub);
  const tot = picked.reduce((a, r) => ({ total: a.total + r.total, active: a.active + r.active }), { total: 0, active: 0 });
  const value = (v: string | null, list: [string, unknown][]) => (v && (list.some(([n]) => n === v) || !q.data) ? v : v ?? '');

  return (
    <>
      {departments.length > 1 && (
        <select className="select" style={{ maxWidth: 220 }} aria-label="Departamento" value={none ? NONE : value(dep, departments)}
          onChange={(e) => set({ department: e.target.value === NONE ? null : e.target.value || null, category: e.target.value === NONE ? NONE : null, subcategory: null, page: null })}>
          <option value="">Todos os departamentos</option>
          {departments.map(([n, c]) => <option key={n} value={n}>{label(n, c)}</option>)}
          {noneCount > 0 && <option value={NONE}>Sem classificação · {int(noneCount)}</option>}
        </select>
      )}
      {!none && (
        <select className="select" style={{ maxWidth: 240 }} aria-label="Categoria" value={value(cat, categories)}
          onChange={(e) => set({ category: e.target.value || null, subcategory: null, page: null })}>
          <option value="">{dep ? `Todas as categorias de ${dep}` : 'Todas as categorias'}</option>
          {categories.map(([n, c]) => <option key={n} value={n}>{label(n, c)}</option>)}
          {departments.length <= 1 && noneCount > 0 && <option value={NONE}>Sem categoria / sem vínculo · {int(noneCount)}</option>}
          {cat && !categories.some(([n]) => n === cat) && <option value={cat}>{cat}</option>}
        </select>
      )}
      {none && departments.length <= 1 && (
        <select className="select" aria-label="Categoria" value={NONE} onChange={(e) => set({ category: e.target.value || null, subcategory: null, page: null })}>
          <option value="">Todas as categorias</option>
          <option value={NONE}>Sem categoria / sem vínculo · {int(noneCount)}</option>
        </select>
      )}
      {subcategories.length > 0 && (
        <select className="select" style={{ maxWidth: 220 }} aria-label="Subcategoria" value={value(sub, subcategories)}
          onChange={(e) => set({ subcategory: e.target.value || null, page: null })}>
          <option value="">Todas as subcategorias</option>
          {subcategories.map(([n, c]) => <option key={n} value={n}>{label(n, c)}</option>)}
        </select>
      )}
      {filtered && q.data && (
        <span className="small" style={{ alignSelf: 'center' }}>
          <b>{int(tot.total)}</b> anúncio{tot.total === 1 ? '' : 's'} · <b>{int(tot.active)}</b> ativo{tot.active === 1 ? '' : 's'}{' '}
          <button type="button" className="linkish" style={{ background: 'none', border: 0, padding: 0, color: 'var(--accent, inherit)', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => set({ department: null, category: null, subcategory: null, page: null })}>limpar</button>
        </span>
      )}
    </>
  );
}

/** Parâmetros de classificação para repassar à exportação. */
export function categoryParams(params: URLSearchParams): string {
  const out = new URLSearchParams();
  for (const k of [...KEYS, 'brand', 'attrs']) if (params.get(k)) out.set(k, params.get(k)!);
  const s = out.toString();
  return s ? `&${s}` : '';
}
